/**
 * SpamSettingsDialog — AI spam filter + blacklist management + auto-cleanup.
 *
 * Toggle the (paid) AI opt-in and edit its rules; manage the whitelist and the
 * two blacklists (senders YOU blocked → mail goes to Trash; senders the AI
 * detected → mail goes to Spam); and configure the auto-cleanup sweep that moves
 * aged Spam into Trash. Nothing here ever permanently deletes mail — Trash is
 * only ever emptied manually.
 */

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Trash2 } from 'lucide-react';
import type { MailboxScreenedSenderDto } from '@/lib/api';
import {
  useSpamSettings,
  useSetSpamSettings,
  useSpamSenders,
  useDeleteSpamSender,
  useSpamSpend,
} from '../hooks/useSpam';
import { useDialogAccount } from '../hooks/useDialogAccount';
import { DialogAccountPicker } from './DialogAccountPicker';

interface SpamSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Seed account; the dialog has its own in-popup picker to switch. */
  defaultAccountId?: string;
}

/** AI-determined blacklist entries route to Spam; everything else is user-owned → Trash. */
function isAiDetermined(s: MailboxScreenedSenderDto): boolean {
  return s.source === 'ai_spam' || s.source === 'failed_auth';
}

/** Credits == cents on the platform (1 credit = $0.01). */
function formatCredits(cents: number): string {
  const credits = cents.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return `${credits} credit${cents === 1 ? '' : 's'}`;
}

/**
 * Blocked/allowed sender list. For the rejected disposition, `kind` splits the
 * shared list into the user-owned vs AI-detected halves (both reuse the one
 * cached `rejected` query — no extra fetch).
 */
function SenderList({
  accountId,
  disposition,
  emptyLabel,
  kind,
}: {
  accountId: string;
  disposition: 'approved' | 'rejected';
  emptyLabel: string;
  kind?: 'user' | 'ai';
}) {
  const { data: all, isLoading } = useSpamSenders(accountId, disposition);
  const remove = useDeleteSpamSender(accountId);
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />;
  const senders = kind
    ? (all ?? []).filter((s) => (kind === 'ai' ? isAiDetermined(s) : !isAiDetermined(s)))
    : (all ?? []);
  if (senders.length === 0) {
    return <p className="py-2 text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <ul className="space-y-1">
      {senders.map((s) => (
        <li
          key={s.screenedId}
          className="flex items-center justify-between gap-2 rounded border px-2 py-1 text-sm"
        >
          <span className="min-w-0">
            <span className="block truncate font-medium">{s.senderName || s.senderAddress}</span>
            {s.reason && (
              <span className="block truncate text-xs text-muted-foreground">{s.reason}</span>
            )}
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Remove ${s.senderAddress}`}
            disabled={remove.isPending}
            onClick={() => remove.mutate(s.screenedId)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

export function SpamSettingsDialog({
  open,
  onOpenChange,
  defaultAccountId,
}: SpamSettingsDialogProps) {
  const { accounts, selectedAccountId, setSelectedAccountId } = useDialogAccount(defaultAccountId);
  const { data: settings, isLoading } = useSpamSettings(selectedAccountId, open);
  // Spend is billed per user (across every account), so it's fetched once.
  const { data: spend } = useSpamSpend(open);
  const save = useSetSpamSettings(selectedAccountId ?? '');
  const [rules, setRules] = useState('');
  const [editingRules, setEditingRules] = useState(false);
  const [autoCleanup, setAutoCleanup] = useState(false);
  const [cleanupDays, setCleanupDays] = useState(30);

  // Seed the editors from the saved settings (or defaults) once loaded.
  // Rules always seed back into read-only view — editing is gated behind Edit.
  useEffect(() => {
    if (settings) {
      setRules(settings.rules ?? settings.defaultRules);
      setEditingRules(false);
      setAutoCleanup(settings.spamRetention.spamToTrash.enabled);
      setCleanupDays(settings.spamRetention.spamToTrash.days);
    }
  }, [settings]);

  const enabled = settings?.enabled ?? false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Spam &amp; AI Filter</DialogTitle>
        </DialogHeader>
        {!selectedAccountId ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Connect an account to use AI spam filtering.
          </p>
        ) : isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-4">
            <DialogAccountPicker
              accounts={accounts}
              value={selectedAccountId}
              onChange={setSelectedAccountId}
            />
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={enabled}
                disabled={save.isPending}
                onChange={(e) => save.mutate({ enabled: e.target.checked })}
              />
              <span>
                Enable AI spam filtering
                <span className="block text-xs text-muted-foreground">
                  Mail from senders you haven’t emailed before is reviewed by AI and moved to Spam
                  if it fails your rules. This uses credits per reviewed sender. Senders you’ve
                  emailed or approved are always allowed.
                </span>
              </span>
            </label>

            {/* What the AI spam filter has cost (user-wide, across accounts). */}
            {spend && (
              <div className="rounded-md border bg-muted/30 p-2 text-xs">
                <p className="mb-1 font-medium text-foreground">AI spam filter spend</p>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                  <dt>Today</dt>
                  <dd className="text-right tabular-nums text-foreground">
                    {formatCredits(spend.dayCents)}
                  </dd>
                  <dt>Last 7 days</dt>
                  <dd className="text-right tabular-nums text-foreground">
                    {formatCredits(spend.weekCents)}
                  </dd>
                  <dt>Last 30 days</dt>
                  <dd className="text-right tabular-nums text-foreground">
                    {formatCredits(spend.monthCents)}
                  </dd>
                  <dt>All time</dt>
                  <dd className="text-right tabular-nums text-foreground">
                    {formatCredits(spend.allCents)}
                  </dd>
                </dl>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Credits spent letting the AI review first-time senders (1 credit = $0.01).
                </p>
              </div>
            )}

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Spam rules</p>
                {!editingRules && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => setEditingRules(true)}
                  >
                    Edit
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Plain-language rules the AI uses to decide what counts as spam.
                {editingRules ? ' Edit to fit your needs.' : ' Click Edit to change them.'}
              </p>
              <Textarea
                rows={8}
                value={rules}
                readOnly={!editingRules}
                disabled={save.isPending}
                onChange={(e) => setRules(e.target.value)}
                aria-label="Spam rules"
                className={
                  editingRules
                    ? 'text-sm'
                    : 'cursor-default bg-muted/40 text-sm text-muted-foreground'
                }
              />
              {editingRules && (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => {
                      if (settings) setRules(settings.rules ?? settings.defaultRules);
                      setEditingRules(false);
                    }}
                  >
                    Cancel
                  </Button>
                  {settings && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={save.isPending}
                      onClick={() => setRules(settings.defaultRules)}
                    >
                      Reset to default
                    </Button>
                  )}
                  <Button
                    size="sm"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({ rules }, { onSuccess: () => setEditingRules(false) })
                    }
                  >
                    {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save rules'}
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">Auto-cleanup</p>
              <p className="text-xs text-muted-foreground">
                Automatically move mail out of Spam into Trash after a set time. Trash is never
                emptied automatically — you always delete permanently yourself.
              </p>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoCleanup}
                  disabled={save.isPending}
                  onChange={(e) => setAutoCleanup(e.target.checked)}
                />
                <span className="flex items-center gap-1">
                  Move Spam to Trash after
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={cleanupDays}
                    disabled={!autoCleanup || save.isPending}
                    onChange={(e) =>
                      setCleanupDays(Math.min(365, Math.max(1, Number(e.target.value) || 1)))
                    }
                    className="w-16 rounded border px-1 py-0.5 text-sm"
                    aria-label="Days before moving Spam to Trash"
                  />
                  days
                </span>
              </label>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  disabled={save.isPending}
                  onClick={() =>
                    save.mutate({
                      spamRetention: { spamToTrash: { enabled: autoCleanup, days: cleanupDays } },
                    })
                  }
                >
                  {save.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    'Save auto-cleanup'
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">You blocked → Trash</p>
              <p className="text-xs text-muted-foreground">
                Senders you reported or rejected. Their mail goes straight to Trash. Remove to allow
                again.
              </p>
              <SenderList
                accountId={selectedAccountId}
                disposition="rejected"
                kind="user"
                emptyLabel="You haven’t blocked any senders yet."
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">AI-detected → Spam</p>
              <p className="text-xs text-muted-foreground">
                Senders the AI filter auto-blocked. Their mail goes to Spam. Remove to review again.
              </p>
              <SenderList
                accountId={selectedAccountId}
                disposition="rejected"
                kind="ai"
                emptyLabel="No AI-detected spammers yet."
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm font-medium">Allowed senders</p>
              <p className="text-xs text-muted-foreground">
                Mail from these addresses is never filtered. Remove to review again.
              </p>
              <SenderList
                accountId={selectedAccountId}
                disposition="approved"
                emptyLabel="No allowed senders yet."
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
